import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { useAuthStore } from '@/stores/auth';
import { useLabel } from '@/lib/labels';
import { formatDateLabel } from '@/lib/format';
import { useHolidays } from '@/hooks/useHolidays';
import { usePreferenceInputMode } from '@/hooks/usePreferenceInputMode';
import { useShiftPatternPresets } from '@/hooks/useShiftPatternPresets';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { DateLabel } from '@/components/DateLabel';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { TimeRangeInput } from '@/components/TimeRangeInput';
import { findNodeInTree, type NodesResponse } from '@/stores/scope';

/**
 * `/shift/preferences` (v0.4 章④ F-1、D-101)。パート層・社員本人が自店のシフト希望を提出する画面。
 * 設計: docs/design/v04-shift-labor-management-detail.md §4-5/§4-10/§4-11
 *
 * ⚠️ GET /shift/periods (組織横断) は self scope を扱わないため (shift-periods.ts 冒頭コメント参照)、
 * この画面は F-1 で新設した `GET /shift-preferences/periods?node_id=` (自分の所属店舗1件に絞った
 * 期間一覧) を使う。所属店舗の特定には `user.stores` (JWT の scope_stores) を使う。
 *
 * ⚠️ v0.4 追加7要件⑤ (D-235): 1日に複数の候補行を持てる (「早番でも遅番でも可」を選好強度付きで
 * 表現する、§2-2(b))。行ごとに shift_pattern_id を選ぶとパターンの実効時刻が流し込まれる (§2-2(a)、
 * ただし start_time/end_time は常に実体化して保存する不変ルール)。入力モード
 * (shift_preference_input_mode) はテナント/ノードのコンフィグ (D-235) で決まる。
 */

type PeriodListEntry = {
  period_id: string;
  label: string;
  start_date: string;
  end_date: string;
  status: string;
  deadline_at: string | null;
  is_past_deadline: boolean;
  submission: { submission_id: string; status: string; amended_after_deadline: boolean } | null;
};

type PreferenceLevel = { level_id: string; code: string; label: string; is_available: boolean; weight: number; sort_order: number };

type PreferenceEntryDetail = {
  preference_id: string;
  target_date: string;
  start_time: string | null;
  end_time: string | null;
  preference_level_id: string;
  shift_pattern_id: string | null;
  note: string | null;
};

type SubmissionDetail = {
  submission_id: string;
  status: string;
  amended_after_deadline: boolean;
  preferences: PreferenceEntryDetail[];
};

/** 1日分の候補行。1日に複数持てる (「早番も可、遅番も可」を別行として表現する、D-235 §2-2(b))。 */
type RowEntry = {
  localId: string;
  levelId: string;
  startTime: string | null;
  endTime: string | null;
  shiftPatternId: string | null;
};

let localIdCounter = 0;
const nextLocalId = (): string => `local-${++localIdCounter}`;

const addDays = (yyyyMmDd: string, days: number): string => {
  const d = new Date(`${yyyyMmDd}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};

const SUBMISSION_STATUS_LABELS: Record<string, string> = {
  draft: '下書き', submitted: '提出済み', amended: '修正申請中',
};

export function ShiftPreferencesPage() {
  const t = useLabel();
  const user = useAuthStore((s) => s.user);
  const qc = useQueryClient();

  const homeStoreIds = useMemo(() => user?.stores ?? [], [user]);
  const [selectedNodeId, setSelectedNodeId] = useState('');
  useEffect(() => {
    if (!selectedNodeId && homeStoreIds.length > 0) setSelectedNodeId(homeStoreIds[0]!);
  }, [homeStoreIds, selectedNodeId]);

  const { data: nodesData } = useQuery<NodesResponse>({
    queryKey: ['nodes'], queryFn: () => apiFetch<NodesResponse>('/nodes'), enabled: homeStoreIds.length > 1,
  });
  const nodeName = (id: string): string => (nodesData ? findNodeInTree(nodesData.roots, id)?.name ?? id : id);

  const { data: periodsData, isLoading: periodsLoading, isError: periodsError } = useQuery<{ periods: PeriodListEntry[] }>({
    queryKey: ['shift-preference-periods', selectedNodeId],
    queryFn: () => apiFetch(`/shift-preferences/periods?node_id=${selectedNodeId}`),
    enabled: !!selectedNodeId,
  });
  const periods = periodsData?.periods ?? [];
  const [selectedPeriodId, setSelectedPeriodId] = useState('');
  useEffect(() => {
    if (periods.length === 0) { setSelectedPeriodId(''); return; }
    // GET /shift-preferences/periods は start_date ASC (一覧表示として自然な古い順) で返る。
    // 既定選択は一覧の先頭 (最も過去) ではなく、最新の期間にする (§8-120)。
    if (!periods.some((p) => p.period_id === selectedPeriodId)) setSelectedPeriodId(periods[periods.length - 1]!.period_id);
  }, [periods, selectedPeriodId]);
  const selectedPeriod = periods.find((p) => p.period_id === selectedPeriodId) ?? null;
  const holidays = useHolidays(selectedPeriod?.start_date ?? '', selectedPeriod?.end_date ?? '', selectedNodeId);

  const { data: levelsData } = useQuery<{ preference_levels: PreferenceLevel[] }>({
    queryKey: ['preference-levels'],
    queryFn: () => apiFetch('/preference-levels'),
  });
  const levels = levelsData?.preference_levels ?? [];

  // v0.4 追加7要件⑤ (D-235): 入力モードに応じてパターン選択 (presets) の出し分け
  const inputMode = usePreferenceInputMode(selectedNodeId || null);
  const { presets } = useShiftPatternPresets(selectedNodeId || null, inputMode !== 'time_range');

  const submissionId = selectedPeriod?.submission?.submission_id ?? null;
  const { data: submissionDetail, isLoading: submissionLoading } = useQuery<SubmissionDetail>({
    queryKey: ['shift-preference-submission', submissionId],
    queryFn: () => apiFetch(`/shift-preferences/${submissionId}`),
    enabled: !!submissionId,
  });

  const [entries, setEntries] = useState<Record<string, RowEntry[]>>({});
  useEffect(() => {
    if (!selectedPeriod) { setEntries({}); return; }
    if (!selectedPeriod.submission) { setEntries({}); return; }
    if (submissionDetail && submissionDetail.submission_id === selectedPeriod.submission.submission_id) {
      const next: Record<string, RowEntry[]> = {};
      for (const p of submissionDetail.preferences) {
        const list = next[p.target_date] ?? (next[p.target_date] = []);
        list.push({
          localId: nextLocalId(), levelId: p.preference_level_id,
          startTime: p.start_time, endTime: p.end_time, shiftPatternId: p.shift_pattern_id,
        });
      }
      setEntries(next);
    }
    // submission はあるが detail 未取得の間は前回の内容を保持し、空へフラッシュしない
  }, [selectedPeriod, submissionDetail]);

  const dateList = useMemo(() => {
    if (!selectedPeriod) return [];
    const out: string[] = [];
    for (let cur = selectedPeriod.start_date; cur <= selectedPeriod.end_date; cur = addDays(cur, 1)) out.push(cur);
    return out;
  }, [selectedPeriod]);

  const buildPayload = () => ({
    preferences: Object.entries(entries).flatMap(([date, rows]) => rows
      .filter((r) => r.levelId)
      .map((r) => ({
        target_date: date, start_time: r.startTime, end_time: r.endTime,
        preference_level_id: r.levelId, shift_pattern_id: r.shiftPatternId,
      }))),
  });

  const invalidateAfterSave = () => {
    qc.invalidateQueries({ queryKey: ['shift-preference-periods', selectedNodeId] });
    if (submissionId) qc.invalidateQueries({ queryKey: ['shift-preference-submission', submissionId] });
  };

  const createMut = useMutation({
    mutationFn: () => apiFetch('/shift-preferences', {
      method: 'POST',
      body: JSON.stringify({ period_id: selectedPeriod!.period_id, node_id: selectedNodeId, ...buildPayload() }),
    }),
    onSuccess: () => { toast.success('希望を提出しました'); invalidateAfterSave(); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '提出に失敗しました'),
  });
  const updateMut = useMutation({
    mutationFn: () => apiFetch(`/shift-preferences/${submissionId}`, { method: 'PUT', body: JSON.stringify(buildPayload()) }),
    onSuccess: () => { toast.success('希望を更新しました'); invalidateAfterSave(); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '更新に失敗しました'),
  });
  const amendMut = useMutation({
    mutationFn: () => apiFetch<{ conflicts: unknown[] }>(`/shift-preferences/${submissionId}/amend-request`, {
      method: 'POST', body: JSON.stringify(buildPayload()),
    }),
    onSuccess: () => { toast.success('修正を申請しました (店舗の確認後に反映されます)'); invalidateAfterSave(); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '修正申請に失敗しました'),
  });

  const saving = createMut.isPending || updateMut.isPending || amendMut.isPending;
  const canSubmitNew = !!selectedPeriod && !selectedPeriod.is_past_deadline;
  const canAmend = !!selectedPeriod && selectedPeriod.is_past_deadline && !!selectedPeriod.submission;
  const blockedPastDeadlineNoSubmission = !!selectedPeriod && selectedPeriod.is_past_deadline && !selectedPeriod.submission;

  const handleSave = () => {
    if (!selectedPeriod) return;
    if (!selectedPeriod.is_past_deadline) {
      if (selectedPeriod.submission) updateMut.mutate(); else createMut.mutate();
    } else if (selectedPeriod.submission) {
      amendMut.mutate();
    }
  };

  const addRow = (date: string) => {
    setEntries((cur) => ({
      ...cur,
      // design-and-code MEDIUM 指摘: start/end を null にすると TimeRangeInput の allowAllDay が
      // 「終日」状態と解釈し、パターン選択・手動入力の両方が非表示になる (「パターン選択のみ」モードでは
      // 新しい候補行を追加した直後に何も選べなくなる)。新規行は既定の時間帯を持たせておく。
      [date]: [...(cur[date] ?? []), { localId: nextLocalId(), levelId: '', startTime: '09:00', endTime: '17:00', shiftPatternId: null }],
    }));
  };
  const removeRow = (date: string, localId: string) => {
    setEntries((cur) => {
      const list = (cur[date] ?? []).filter((r) => r.localId !== localId);
      const next = { ...cur };
      if (list.length === 0) delete next[date]; else next[date] = list;
      return next;
    });
  };
  const setRowLevel = (date: string, localId: string, levelId: string) => {
    setEntries((cur) => ({
      ...cur,
      [date]: (cur[date] ?? []).map((r) => {
        if (r.localId !== localId) return r;
        const level = levels.find((l) => l.level_id === levelId);
        const unavailable = level ? !level.is_available : false;
        return {
          ...r, levelId,
          startTime: unavailable ? null : r.startTime, endTime: unavailable ? null : r.endTime,
          shiftPatternId: unavailable ? null : r.shiftPatternId,
        };
      }),
    }));
  };
  // ⚠️ D-235/Q-179: 時刻の手修正だけを行い shiftPatternId には触れない。パターン選択後に手修正しても
  // 「由来」として shift_pattern_id を保持する (ShiftStandingPatternsPage/ShiftBoardPage と同じ挙動)。
  const setRowTime = (date: string, localId: string, startTime: string | null, endTime: string | null) => {
    setEntries((cur) => ({
      ...cur,
      [date]: (cur[date] ?? []).map((r) => (r.localId === localId ? { ...r, startTime, endTime } : r)),
    }));
  };
  // v0.4 追加7要件⑤ (D-235): TimeRangeInput の presets 機構が onChange (時刻反映) と
  // onPresetSelect (由来記録) を別々に呼ぶため、由来記録だけをここで受ける。
  const setRowPattern = (date: string, localId: string, patternId: string) => {
    setEntries((cur) => ({ ...cur, [date]: (cur[date] ?? []).map((r) => (r.localId === localId ? { ...r, shiftPatternId: patternId } : r)) }));
  };

  if (homeStoreIds.length === 0) {
    return (
      <div className="space-y-4">
        <h2 className="text-h2">{t('entity.shift_preferences', 'シフト希望')}</h2>
        <EmptyState
          message="所属店舗が設定されていません"
          description="シフト希望を提出するには、所属店舗の割当が必要です。管理者にご相談ください。"
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-h2">{t('entity.shift_preferences', 'シフト希望')}</h2>
        <p className="text-sm text-text-sub mt-1">当社分の希望のみを提出できます。</p>
      </div>

      {homeStoreIds.length > 1 && (
        <div>
          <label className="block text-sm text-text-sub mb-1">対象店舗</label>
          <Select value={selectedNodeId} onValueChange={setSelectedNodeId}>
            <SelectTrigger className="w-full sm:w-64 min-h-[44px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {homeStoreIds.map((id) => <SelectItem key={id} value={id}>{nodeName(id)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      {periodsLoading ? (
        <div className="text-text-sub">読み込み中…</div>
      ) : periodsError ? (
        <div className="p-6 border border-surface-border rounded-lg bg-white text-text-sub">期間の取得に失敗しました。</div>
      ) : periods.length === 0 ? (
        <EmptyState
          message="現在提出できるシフト期間がありません"
          description="新しい期間が始まると、ここから希望を提出できるようになります。"
        />
      ) : (
        <>
          <div>
            <label className="block text-sm text-text-sub mb-1">対象期間</label>
            <Select value={selectedPeriodId} onValueChange={setSelectedPeriodId}>
              <SelectTrigger className="w-full sm:w-80 min-h-[44px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {periods.map((p) => (
                  <SelectItem key={p.period_id} value={p.period_id}>
                    {p.label} ({formatDateLabel(p.start_date)}〜{formatDateLabel(p.end_date)})
                    {p.submission ? ` · ${SUBMISSION_STATUS_LABELS[p.submission.status] ?? p.submission.status}` : ' · 未提出'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedPeriod && (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-text-sub">
              {selectedPeriod.deadline_at && (
                <span className={selectedPeriod.is_past_deadline ? 'text-alert-text font-semibold' : ''}>
                  締切: {formatDateLabel(selectedPeriod.deadline_at)}
                  {selectedPeriod.is_past_deadline ? ' (締切を過ぎています)' : ''}
                </span>
              )}
              {selectedPeriod.submission && (
                <span>状態: {SUBMISSION_STATUS_LABELS[selectedPeriod.submission.status] ?? selectedPeriod.submission.status}</span>
              )}
            </div>
          )}

          {blockedPastDeadlineNoSubmission ? (
            <div className="p-4 border border-alert-border bg-alert-bg rounded-lg text-sm text-alert-text">
              締切を過ぎており、まだ希望の提出がありません。店舗の管理者にご相談ください。
            </div>
          ) : submissionId && submissionLoading ? (
            <div className="text-text-sub">読み込み中…</div>
          ) : (
            <>
              {canAmend && (
                <div className="p-3 border border-alert-border bg-alert-bg rounded-lg text-xs text-alert-text">
                  締切を過ぎています。保存すると「修正申請」として送られ、店舗の確認後に反映されます。
                </div>
              )}
              <p className="text-xs text-text-sub">
                「候補を追加」で1日に複数の候補 (例: 早番も遅番も可) を登録できます。選好レベルで優先度を表せます。
              </p>
              <div className="border border-surface-border rounded-lg bg-white overflow-hidden">
                {dateList.map((date) => {
                  const rows = entries[date] ?? [];
                  return (
                    <div key={date} className="py-2.5 px-3 border-b border-surface-border last:border-b-0 space-y-2">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="text-sm text-text-body whitespace-nowrap"><DateLabel date={date} withWeekday holidays={holidays} /></div>
                        <Button variant="ghost" size="sm" leftIcon={<Plus size={14} />} onClick={() => addRow(date)}>候補を追加</Button>
                      </div>
                      {rows.length === 0 ? (
                        <p className="text-xs text-text-sub">指定なし</p>
                      ) : (
                        <div className="space-y-2">
                          {rows.map((row) => {
                            const level = levels.find((l) => l.level_id === row.levelId);
                            const rowShowTime = !!row.levelId && (level ? level.is_available : true);
                            return (
                              // design-expert HIGH-2 指摘: 候補が複数件になると折返し時にどこまでが
                              // 1候補か区別できなかった (行間gapと折返し時のgapが同値のため)。
                              // カード化して境界を明示する。
                              <div key={row.localId} className="flex flex-wrap items-center gap-2 p-2 rounded-md border border-surface-line bg-surface-sunken/40">
                                <Select value={row.levelId || undefined} onValueChange={(v) => setRowLevel(date, row.localId, v)}>
                                  <SelectTrigger className="w-[150px] min-h-[44px]"><SelectValue placeholder="選ぶ" /></SelectTrigger>
                                  <SelectContent>
                                    {levels.map((l) => <SelectItem key={l.level_id} value={l.level_id}>{l.label}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                                {rowShowTime && (
                                  <TimeRangeInput
                                    value={{ startTime: row.startTime, endTime: row.endTime }}
                                    onChange={(v) => setRowTime(date, row.localId, v.startTime, v.endTime)}
                                    allowAllDay
                                    presets={presets}
                                    selectedPresetId={row.shiftPatternId}
                                    onPresetSelect={(id) => setRowPattern(date, row.localId, id)}
                                    hideManualInput={inputMode === 'pattern'}
                                  />
                                )}
                                <button
                                  type="button" aria-label={`${formatDateLabel(date)}の候補を削除`} onClick={() => removeRow(date, row.localId)}
                                  className="w-11 h-11 inline-flex items-center justify-center rounded text-text-sub hover:text-danger-text focus-ring shrink-0"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="flex justify-end">
                <Button variant="primary" loading={saving} disabled={!canSubmitNew && !canAmend} onClick={handleSave}>
                  {canAmend ? '修正を申請する' : selectedPeriod?.submission ? '更新する' : '提出する'}
                </Button>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
